<!-- Panorama.vue -->
<template>
  <div id="panorama-container">
    <!-- 全景图容器 -->
    <div id="panorama" ref="panorama"></div>

    <!-- 视频播放模态框 -->
    <div v-if="showVideoModal" class="video-modal" @click="closeVideo">
      <div class="video-modal-content" @click.stop>
        <button class="close-btn video-close-btn" @click="closeVideo">&times;</button>
        <video ref="videoPlayer" :src="currentVideoSrc" controls autoplay class="video"></video>
      </div>
    </div>

    <!-- 图片展示模态框 -->
    <div v-if="showImageModal" class="image-modal" @click="closeImage">
      <button class="close-btn image-close-btn" @click="closeImage">&times;</button>
      <div 
        class="image-modal-content" 
        @click.stop 
        @wheel="handleImageWheel"
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="handleTouchEnd"
      >
        <img 
          :src="currentImageSrc" 
          alt="展示图片" 
          class="modal-image"
          :style="getImageStyle()"
          @mousedown="startDrag"
          @mousemove="onDrag"
          @mouseup="stopDrag"
          @mouseleave="stopDrag"
          draggable="false"
        />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PannellumView",
  props: {
    config: {
      type: Object,
      default: () => ({}),
    },
    panoramaImage: {
      type: [String, Object],
      default: () => 'https://online-1368622751.cos.ap-shanghai.myqcloud.com/assets/3.jpg'
    },
    hotspots: {
      type: Array,
      default: () => []
    },
    videoUrl: {
      type: String,
      default: 'http://vjs.zencdn.net/v/oceans.mp4'
    },
    // 初始视角配置
    initialView: {
      type: Object,
      default: () => ({
        pitch: -3,
        yaw: 117,
        hfov: 110
      })
    }
  },
  data() {
    return {
      panoramaViewer: null,
      loadingStarted: false,
      loadingCompleted: false,
      showVideoModal: false,
      showImageModal: false,
      currentVideoSrc: '',
      currentImageSrc: '',
      resizeTimer: null,
      isFirstLoad: true,
      // 图片缩放相关
      imageScale: 1,
      imageTranslateX: 0,
      imageTranslateY: 0,
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      // 触摸缩放相关
      initialDistance: 0,
      initialScale: 1,
      // 热点检查定时器
      hotspotCheckInterval: null
    };
  },
  watch: {
    // 只监听 panoramaImage 变化，避免深度监听整个 config 对象
    panoramaImage: {
      handler(newImage, oldImage) {
        // 首次加载时不处理，避免重复初始化
        if (this.isFirstLoad) {
          return;
        }

        // 确保新旧值不同才重新初始化
        if (newImage !== oldImage) {
          this.reinitializeViewer();
        }
      }
    },
    // 监听 hotspots 变化
    hotspots: {
      handler(newHotspots, oldHotspots) {
        if (this.isFirstLoad) {
          return;
        }
        
        // 当 hotspots 变化时，重新初始化以应用新的点位
        if (JSON.stringify(newHotspots) !== JSON.stringify(oldHotspots)) {
          this.reinitializeViewer();
        }
      },
      deep: true
    },
    // 监听 initialView 变化
    initialView: {
      handler(newView, oldView) {
        if (this.isFirstLoad) {
          return;
        }
        
        // 当 initialView 变化时，重新初始化以应用新的视角
        if (JSON.stringify(newView) !== JSON.stringify(oldView)) {
          this.reinitializeViewer();
        }
      },
      deep: true
    }
  },
  mounted() {
    this.loadingStarted = false;
    this.loadingCompleted = false;

    if (typeof window.pannellum !== 'undefined') {
      this.initPannellum();
    } else {
      this.loadPannellumScript().then(() => {
        this.initPannellum();
      }).catch(error => {
        console.error('Failed to load Pannellum:', error);
      });
    }

    window.addEventListener('resize', this.handleResize);
    
    // 在初始化完成后标记首次加载完成
    this.$nextTick(() => {
      setTimeout(() => {
        this.isFirstLoad = false;
      }, 500);
    });
  },
  beforeDestroy() {
    this.cleanup();
    window.removeEventListener('resize', this.handleResize);

    // 清理定时器
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    
    // 清理热点检查定时器
    if (this.hotspotCheckInterval) {
      clearInterval(this.hotspotCheckInterval);
      this.hotspotCheckInterval = null;
    }
  },
  methods: {
    cleanup() {
      // 清理视频播放器
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.pause();
        this.$refs.videoPlayer.src = '';
        this.$refs.videoPlayer.load();
      }

      // 清理全景图查看器
      if (this.panoramaViewer) {
        try {
          this.panoramaViewer.destroy();
        } catch (e) {
          console.warn('Error destroying panorama viewer:', e);
        }
        this.panoramaViewer = null;
      }
    },

    reinitializeViewer() {
      this.cleanup();
      this.loadingStarted = false;
      this.loadingCompleted = false;

      this.$nextTick(() => {
        this.initPannellum();
      });
    },

    handleResize() {
      // 防抖处理窗口大小变化
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }

      this.resizeTimer = setTimeout(() => {
        if (this.panoramaViewer && typeof this.panoramaViewer.resize === 'function') {
          this.panoramaViewer.resize();
        }
      }, 300);
    },

    // 这个方法已经不需要了，因为 hotspots 现在直接在 initPannellum 中配置

    playVideo(videoSrc) {
      this.currentVideoSrc = videoSrc;
      this.showVideoModal = true;
      
      // 通知父组件视频开始播放
      this.$emit('video-play');
      
      this.$nextTick(() => {
        if (this.$refs.videoPlayer) {
          this.$refs.videoPlayer.play();
        }
      });
    },

    showImage(imageSrc) {
      this.currentImageSrc = imageSrc;
      this.showImageModal = true;
      this.resetImageTransform();
    },

    closeVideo() {
      this.showVideoModal = false;
      
      // 通知父组件视频关闭
      this.$emit('video-close');
      
      this.$nextTick(() => {
        if (this.$refs.videoPlayer) {
          this.$refs.videoPlayer.pause();
          this.$refs.videoPlayer.currentTime = 0;
        }
      });
    },

    closeImage() {
      this.showImageModal = false;
      this.resetImageTransform();
    },

    // 图片缩放和拖拽相关方法
    resetImageTransform() {
      this.imageScale = 1;
      this.imageTranslateX = 0;
      this.imageTranslateY = 0;
      this.isDragging = false;
      this.initialDistance = 0;
      this.initialScale = 1;
    },

    handleImageWheel(event) {
      event.preventDefault();
      const delta = event.deltaY > 0 ? -0.1 : 0.1;
      this.imageScale = Math.max(0.5, Math.min(5, this.imageScale + delta));
      this.constrainImagePosition();
    },

    // 触摸事件处理
    handleTouchStart(event) {
      if (event.touches.length === 2) {
        // 双指触摸开始
        event.preventDefault();
        this.initialDistance = this.getTouchDistance(event.touches);
        this.initialScale = this.imageScale;
      } else if (event.touches.length === 1 && this.imageScale > 1) {
        // 单指拖拽（仅在放大时）
        this.isDragging = true;
        this.dragStartX = event.touches[0].clientX - this.imageTranslateX;
        this.dragStartY = event.touches[0].clientY - this.imageTranslateY;
      }
    },

    handleTouchMove(event) {
      if (event.touches.length === 2) {
        // 双指缩放
        event.preventDefault();
        const currentDistance = this.getTouchDistance(event.touches);
        const scale = (currentDistance / this.initialDistance) * this.initialScale;
        this.imageScale = Math.max(0.5, Math.min(5, scale));
        this.constrainImagePosition();
      } else if (event.touches.length === 1 && this.isDragging) {
        // 单指拖拽
        event.preventDefault();
        this.imageTranslateX = event.touches[0].clientX - this.dragStartX;
        this.imageTranslateY = event.touches[0].clientY - this.dragStartY;
      }
    },

    handleTouchEnd(event) {
      if (event.touches.length < 2) {
        this.initialDistance = 0;
      }
      if (event.touches.length === 0) {
        this.isDragging = false;
      }
    },

    getTouchDistance(touches) {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    },

    startDrag(event) {
      if (this.imageScale > 1) {
        this.isDragging = true;
        this.dragStartX = event.clientX - this.imageTranslateX;
        this.dragStartY = event.clientY - this.imageTranslateY;
        event.preventDefault();
      }
    },

    onDrag(event) {
      if (this.isDragging) {
        this.imageTranslateX = event.clientX - this.dragStartX;
        this.imageTranslateY = event.clientY - this.dragStartY;
        event.preventDefault();
      }
    },

    stopDrag() {
      this.isDragging = false;
    },

    constrainImagePosition() {
      // 当缩放比例较小时，重置位置到中心
      if (this.imageScale <= 1) {
        this.imageTranslateX = 0;
        this.imageTranslateY = 0;
      }
    },

    getImageStyle() {
      return {
        transform: `translate(${this.imageTranslateX}px, ${this.imageTranslateY}px) scale(${this.imageScale})`,
        cursor: this.imageScale > 1 ? (this.isDragging ? 'grabbing' : 'grab') : 'default',
        transition: this.isDragging ? 'none' : 'transform 0.2s ease'
      };
    },

    loadPannellumScript() {
      return new Promise((resolve, reject) => {
        if (document.querySelector('script[src*="pannellum"]')) {
          setTimeout(() => {
            if (typeof window.pannellum !== 'undefined') {
              resolve();
            } else {
              reject(new Error('Pannellum script already exists but not loaded'));
            }
          }, 1000);
          return;
        }

        const cssLink = document.createElement('link');
        cssLink.rel = 'stylesheet';
        cssLink.href = './pannellum.css';
        document.head.appendChild(cssLink);

        const script = document.createElement('script');
        script.src = './pannellum.js';
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },

    initPannellum() {
      if (window.pannellum && typeof window.pannellum.viewer === 'function') {
        this.$nextTick(() => {
          if (this.$refs.panorama) {
            try {
              this.cleanup();

              // 构建 hotSpots 配置
              const hotSpots = this.hotspots.map((hotspot, index) => {
                return {
                  id: hotspot.id || `hotspot-${index}`,
                  pitch: hotspot.pitch,
                  yaw: hotspot.yaw,
                  type: hotspot.type || 'info',
                  text: hotspot.text || '',
                  clickHandlerFunc: () => {
                    if (hotspot.videoSrc) {
                      this.playVideo(hotspot.videoSrc);
                    } else if (hotspot.imageSrc) {
                      this.showImage(hotspot.imageSrc);
                    }
                  }
                };
              });

              // 获取初始视角配置，确保使用正确的值
              const initialHfov = this.initialView && typeof this.initialView.hfov === 'number' ? this.initialView.hfov : 110;
              const initialPitch = this.initialView && typeof this.initialView.pitch === 'number' ? this.initialView.pitch : -3;
              const initialYaw = this.initialView && typeof this.initialView.yaw === 'number' ? this.initialView.yaw : 117;

              // 构建配置对象
              const config = {
                default: {
                  firstScene: "scene0",
                  sceneFadeDuration: 100,
                  autoLoad: true,
                  autoRotate: -2,
                  showControls: false,
                  draggable: true,
                  disableKeyboardCtrl: true,
                  touchPanSpeedCoeffFactor: 1.5,
                  friction: 0.15,
                },
                scenes: {
                  scene0: {
                    hfov: initialHfov,
                    pitch: initialPitch,
                    yaw: initialYaw,
                    type: "equirectangular",
                    panorama: this.panoramaImage,
                    hotSpots: hotSpots,
                  }
                }
              };

              // 添加 onload 回调
              config.default.onload = () => {
                this.onPanoramaLoaded();
                this.$emit('panorama-loaded', this.panoramaViewer);
              };

              this.panoramaViewer = window.pannellum.viewer(this.$refs.panorama, config);
              this.loadingStarted = true;

              if (this.panoramaViewer) {
                this.checkLoadingStatusAccurate();
              }
            } catch (error) {
              console.error('Error creating Pannellum viewer:', error);
            }
          } else {
            console.error('Panorama container not found');
          }
        });
      } else {
        console.error('Pannellum not available on window object');
      }
    },

    checkLoadingStatusAccurate() {
      if (!this.panoramaViewer || this.loadingCompleted) return;

      let checkCount = 0;
      const maxChecks = 50;

      const check = () => {
        checkCount++;
        if (checkCount > maxChecks) {
          console.warn('Max check attempts reached');
          return;
        }

        try {
          if (!this.panoramaViewer) {
            setTimeout(check, 200);
            return;
          }

          const yaw = this.panoramaViewer.getYaw();
          const hfov = this.panoramaViewer.getHfov();

          if (typeof yaw === 'number' && !isNaN(yaw) &&
            typeof hfov === 'number' && !isNaN(hfov)) {
            this.onPanoramaLoaded();
            this.$emit('panorama-loaded', this.panoramaViewer);
            this.loadingCompleted = true;
            
            // 延迟替换热点图标，确保DOM已渲染
            setTimeout(() => {
              this.replaceHotspotIcons();
            }, 100);
            
            // 再次延迟确保替换成功
            setTimeout(() => {
              this.replaceHotspotIcons();
            }, 1000);
            
            return;
          }

          setTimeout(check, 200);
        } catch (e) {
          if (checkCount < 10) {
            setTimeout(check, 200);
          }
        }
      };

      setTimeout(check, 200);
    },

    onPanoramaLoaded() {
      // 动态修改热点图标为问号
      this.$nextTick(() => {
        setTimeout(() => {
          this.replaceHotspotIcons();
        }, 100);
        
        setTimeout(() => {
          this.replaceHotspotIcons();
        }, 500);
        
        setTimeout(() => {
          this.replaceHotspotIcons();
        }, 1000);
      });
    },

    // 动态替换热点图标
    replaceHotspotIcons() {
      const hotspots = document.querySelectorAll('.pnlm-hotspot.pnlm-info');
      console.log('找到热点数量:', hotspots.length);
      
      hotspots.forEach((hotspot, index) => {
        console.log(`处理热点 ${index + 1}，当前内容: "${hotspot.textContent}"`);
        
        // 强制设置放大镜图标内容
        hotspot.textContent = '🔍';
        hotspot.innerHTML = '🔍';
        
        // 强制应用样式
        const styles = {
          'background-image': 'none',
          'background-position': 'initial',
          'background-color': 'rgba(0, 0, 0, 0.9)',
          'border-radius': '50%',
          'border': 'none',
          'color': 'white',
          'padding': '0',
          'white-space': 'nowrap',
          'width': '20px',
          'height': '20px',
          'text-indent': '0',
          'font-size': '12px',
          'display': 'flex',
          'align-items': 'center',
          'justify-content': 'center',
          'font-weight': 'bold',
          'text-align': 'center',
          'line-height': 'normal'
        };
        
        Object.keys(styles).forEach(property => {
          hotspot.style.setProperty(property, styles[property], 'important');
        });
        
        console.log(`已将热点 ${index + 1} 设置为放大镜，最终内容: "${hotspot.textContent}"`);
      });
      
      // 设置定时器持续检查和替换新出现的热点
      if (!this.hotspotCheckInterval) {
        this.hotspotCheckInterval = setInterval(() => {
          const currentHotspots = document.querySelectorAll('.pnlm-hotspot.pnlm-info');
          currentHotspots.forEach((hotspot, index) => {
            if (hotspot.textContent !== '🔍') {
              console.log(`定时器检查：热点 ${index + 1} 内容不是放大镜，重新设置`);
              this.replaceHotspotIcons();
            }
          });
        }, 500);
      }
    }
  },
};
</script>

<style scoped>
.video {
  width: 650px;
  height: 366px;
}

#panorama-container {
  position: relative;
  width: 100%;
  height: 100vh;
}

#panorama {
  width: 100%;
  height: 100%;
}

/* 隐藏 Pannellum 的加载进度条和加载动画 */
#panorama :deep(.pnlm-load-box) {
  display: none !important;
}

#panorama :deep(.pnlm-load-button) {
  display: none !important;
}

#panorama :deep(.pnlm-lbox) {
  display: none !important;
}

#panorama :deep(.pnlm-lbar) {
  display: none !important;
}

#panorama :deep(.pnlm-lmsg) {
  display: none !important;
}

#panorama :deep(.pnlm-loading) {
  display: none !important;
}

.pnlm-hotspot {
  position: absolute;
  visibility: hidden;
  cursor: pointer;
  transform: translate(-50%, -100%);
  /* 隐藏默认的背景图标 */
  background-image: none !important;
  background: none !important;
}

/* 强制覆盖 Pannellum 的默认热点样式 */
#panorama .pnlm-hotspot {
  background-image: none !important;
  background: none !important;
}

.pnlm-hotspot.pnlm-active {
  visibility: visible;
}

/* 恢复原来的热点样式，显示放大镜 */
.pnlm-hotspot.pnlm-info,
#panorama .pnlm-hotspot.pnlm-info,
#panorama-container .pnlm-hotspot.pnlm-info {
  background-image: none !important;
  background-position: initial !important;
  background-color: rgba(0, 0, 0, 0.9) !important;
  border-radius: 50% !important;
  border: none !important;
  color: white !important;
  padding: 0 !important;
  white-space: nowrap !important;
  width: 24px !important;
  height: 24px !important;
  text-indent: 0 !important;
  font-size: 14px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  font-weight: normal !important;
  text-align: center !important;
  line-height: normal !important;
}

/* 移除任何伪元素 */
.pnlm-hotspot.pnlm-info::before,
.pnlm-hotspot.pnlm-info::after,
#panorama .pnlm-hotspot.pnlm-info::before,
#panorama .pnlm-hotspot.pnlm-info::after,
#panorama-container .pnlm-hotspot.pnlm-info::before,
#panorama-container .pnlm-hotspot.pnlm-info::after {
  content: none !important;
  display: none !important;
}

/* 热点悬停效果 */
.pnlm-hotspot.pnlm-info:hover,
#panorama .pnlm-hotspot.pnlm-info:hover {
  background-color: rgba(0, 0, 0, 0.9) !important;
}



.video-modal, .image-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.video-modal-content {
  position: relative;
  max-width: 90%;
  max-height: 90%;
  overflow: visible;
}

.image-modal-content {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: visible;
}

.modal-image {
  max-width: 100vw;
  max-height: 100vh;
  object-fit: contain;
  user-select: none;
  -webkit-user-drag: none;
  transform-origin: center center;
}

.close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.7);
  border: none;
  font-size: 20px;
  color: white;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1002;
}

.close-btn:hover {
  background-color: rgba(0, 0, 0, 0.9);
}

.video-close-btn {
  position: absolute;
  top: -15px;
  right: -15px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: white;
  border: none;
  font-size: 20px;
  color: black;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1003;
}

.video-close-btn:hover {
  background-color: #f2f2f2;
}

.image-close-btn {
  position: fixed;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  font-size: 24px;
  z-index: 1001;
}
</style>